import React, { useState } from 'react';
import OrigemBadge from '../../components/OrigemBadge';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, Search, Filter, DollarSign, Tag } from 'lucide-react';
import { AcoesDaLinha, BotaoVer, BotaoEditar, BotaoExcluir, BotaoPagar } from '../../components/BotoesAcao';
import { despesasService, DespesasFilters } from '../../services/despesas';
import ExportButtons from '../../components/ExportButtons';
import { useConfirm, usePrompt } from '../../components/ConfirmDialog';
import { exportService } from '../../services/export';
import { Despesa } from '../../types';
import { formatCurrency, formatDate, formatExpenseCategory } from '../../utils/formatters';
import { categoriasDespesaService } from '../../services/categoriasDespesa';
import { usePermissoes } from '../../hooks/usePermissoes';
import AvisoSemPermissao from '../../components/AvisoSemPermissao';
import { ehSemPermissao, mensagemDoErro } from '../../utils/erros';
// Componentes UI básicos usando elementos HTML

const DespesasList: React.FC = () => {
  const { pode } = usePermissoes();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const showPrompt = usePrompt();
  
  const [filters, setFilters] = useState<DespesasFilters>({
    per_page: 15,
    sort_by: 'data_despesa',
    sort_order: 'desc'
  });

  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['despesas', filters],
    queryFn: () => despesasService.getDespesas(filters),
    placeholderData: (previousData) => previousData
  });

  // O catalogo da empresa: e dele que sai o nome de cada categoria, no selo e
  // no filtro. Um mapa fixo na tela ignoraria renomeacao e cadastro novo.
  const { data: categoriasData } = useQuery({
    queryKey: ['categorias-despesa'],
    queryFn: categoriasDespesaService.listar,
  });

  const categorias = categoriasData?.data ?? [];

  /**
   * O nome da categoria, com reserva para o que nao esta no catalogo.
   *
   * Slug orfao existe: despesa anterior ao catalogo, ou categoria excluida
   * direto no banco. Mostrar o texto legivel e melhor do que o slug cru, e
   * muito melhor do que sumir — o valor dela esta somado no total.
   */
  const nomeDaCategoria = (slug: string) =>
    categorias.find((c) => c.slug === slug)?.nome ?? formatExpenseCategory(slug);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => despesasService.deleteDespesa(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['despesas'] });
      toast.success('Despesa excluída com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir despesa');
    }
  });

  const markAsPaidMutation = useMutation({
    mutationFn: ({ id, formaPagamento }: { id: number; formaPagamento: string }) => 
      despesasService.marcarComoPaga(id, formaPagamento),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['despesas'] });
      toast.success('Despesa marcada como paga!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao marcar despesa como paga');
    }
  });

  const handleFilterChange = (key: keyof DespesasFilters, value: any) => {
    // Voltar para a primeira pagina ao mudar um filtro esta certo: o resultado
    // e outro e a pagina 5 do resultado anterior nao significa nada. Mas isso
    // nao pode valer quando a chave alterada E a pagina, senao o botao Proxima
    // pede a pagina 2 e a linha seguinte devolve para a 1 — a paginacao fica
    // inutilizada sem nenhum sinal de erro.
    setFilters(prev => ({
      ...prev,
      [key]: value,
      ...(key === 'page' ? {} : { page: 1 }),
    }));
  };

  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      fornecedor: searchTerm,
      page: 1
    }));
  };

  const clearFilters = () => {
    setFilters({
      per_page: 15,
      sort_by: 'data_despesa',
      sort_order: 'desc'
    });
    setSearchTerm('');
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: 'Excluir despesa',
      message: 'Tem certeza que deseja excluir esta despesa? Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir',
      variant: 'danger',
    });
    if (ok) {
      deleteMutation.mutate(id);
    }
  };

  const handleMarkAsPaid = async (id: number) => {
    const formaPagamento = await showPrompt({
      title: 'Marcar como paga',
      message: 'Selecione a forma de pagamento:',
      confirmLabel: 'Confirmar',
      variant: 'primary',
      input: {
        type: 'select',
        label: 'Forma de pagamento',
        defaultValue: 'pix',
        options: [
          { value: 'pix', label: 'PIX' },
          { value: 'credito', label: 'Cartão de Crédito' },
          { value: 'debito', label: 'Cartão de Débito' },
          { value: 'dinheiro', label: 'Dinheiro' },
        ],
      },
    });
    if (formaPagamento) {
      markAsPaidMutation.mutate({ id, formaPagamento });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      paga: 'bg-green-100 text-green-800',
      pendente: 'bg-yellow-100 text-yellow-800',
      vencida: 'bg-red-100 text-red-800',
      cancelada: 'bg-gray-100 text-gray-800'
    } as const;

    const labels = {
      paga: 'Paga',
      pendente: 'Pendente',
      vencida: 'Vencida',
      cancelada: 'Cancelada'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status as keyof typeof labels] || status}
      </span>
    );
  };

  const getCategoriaBadge = (categoria: string) => {
    const colors = {
      combustivel: 'bg-blue-100 text-blue-800',
      manutencao: 'bg-orange-100 text-orange-800',
      seguro: 'bg-green-100 text-green-800',
      ipva: 'bg-purple-100 text-purple-800',
      licenciamento: 'bg-yellow-100 text-yellow-800',
      multa: 'bg-red-100 text-red-800',
      pedagio: 'bg-indigo-100 text-indigo-800',
      estacionamento: 'bg-pink-100 text-pink-800',
      lavagem: 'bg-cyan-100 text-cyan-800',
      financiamento: 'bg-gray-100 text-gray-800',
      depreciacao: 'bg-slate-100 text-slate-800',
      outros: 'bg-gray-100 text-gray-800'
    };

    // O nome vem do catalogo da empresa: um mapa fixo aqui mostraria "Pedágio"
    // depois de o gestor ter renomeado para "Pedágios e balanças", e o slug cru
    // para as categorias que ela criou.
    //
    // A cor continua fixa por slug, porque e enfeite: categoria propria cai no
    // cinza e isso nao atrapalha ninguem.
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[categoria as keyof typeof colors] || colors.outros}`}>
        {nomeDaCategoria(categoria)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    // Falta de permissão não é falha: recarregar não resolve, e o usuário
    // precisa saber que o caminho é pedir o acesso a quem administra.
    if (ehSemPermissao(error)) {
      return <AvisoSemPermissao erro={error} />;
    }

    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{mensagemDoErro(error, 'Erro ao carregar despesas')}</p>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const despesas = data?.data || [];
  // A API devolve os campos de paginacao dentro de `pagination`, nao na raiz.
  // Ler da raiz deixava last_page indefinido e o rodape nunca aparecia.
  const pagination = data?.pagination ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Despesas</h1>
          <p className="text-gray-600">Controle financeiro dos veículos</p>
        </div>
        <div className="flex items-center gap-3">
          {pode('relatorios', 'exportar') && (
            <ExportButtons
            onExportPdf={() => exportService.despesasPdf()}
            onExportExcel={() => exportService.despesasExcel()}
          />
          )}
          {/* O catalogo fica ao lado de onde ele e usado: quem descobre que
              falta uma categoria descobre lancando uma despesa. */}
          <button
            onClick={() => navigate('/despesas/categorias')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Tag className="w-4 h-4 mr-2" />
            Categorias
          </button>
          <button
            onClick={() => navigate('/despesas/novo')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova despesa
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria
              </label>
              <select
                value={filters.categoria || ''}
                onChange={(e) => handleFilterChange('categoria', e.target.value || undefined)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {/* Do catalogo: sem isto, a categoria que a empresa criou nao
                    teria como ser filtrada, e a que ela apagou continuaria
                    oferecida. */}
                <option value="">Todas as categorias</option>
                {categorias.map((categoria) => (
                  <option key={categoria.id} value={categoria.slug}>
                    {categoria.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="">Todos os status</option>
                <option value="paga">Paga</option>
                <option value="pendente">Pendente</option>
                <option value="vencida">Vencida</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo
              </label>
              <select
                value={filters.tipo || ''}
                onChange={(e) => handleFilterChange('tipo', e.target.value || undefined)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="">Todos os tipos</option>
                <option value="fixa">Fixa</option>
                <option value="variavel">Variável</option>
                <option value="eventual">Eventual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ordenar por
              </label>
              <select
                value={`${filters.sort_by}-${filters.sort_order}`}
                onChange={(e) => {
                  const [sort_by, sort_order] = e.target.value.split('-');
                  handleFilterChange('sort_by', sort_by);
                  handleFilterChange('sort_order', sort_order as 'asc' | 'desc');
                }}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="data_despesa-desc">Data (mais recente)</option>
                <option value="data_despesa-asc">Data (mais antiga)</option>
                <option value="valor-desc">Valor (maior)</option>
                <option value="valor-asc">Valor (menor)</option>
                <option value="descricao-asc">Descrição (A-Z)</option>
                <option value="descricao-desc">Descrição (Z-A)</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar por fornecedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <button 
              onClick={handleSearch}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Search className="w-4 h-4" />
            </button>
            <button 
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Despesas */}
      {despesas.length === 0 ? (
        <div className="text-center py-12">
          <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma despesa encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">Não há despesas que correspondam aos filtros selecionados.</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Despesa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Veículo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {despesas.map((despesa: Despesa) => (
                  <tr key={despesa.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          {despesa.descricao}
                          <OrigemBadge origem={despesa.origem} />
                        </div>
                        <div className="text-sm text-gray-500">
                          {despesa.fornecedor}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {despesa.veiculo?.placa} - {despesa.veiculo?.marca} {despesa.veiculo?.modelo}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getCategoriaBadge(despesa.categoria)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(despesa.valor)}
                      </div>
                      {despesa.total_parcelas && despesa.total_parcelas > 1 && (
                        <div className="text-xs text-gray-500">
                          {despesa.parcela_atual}/{despesa.total_parcelas} parcelas
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(despesa.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{formatDate(despesa.data_despesa)}</div>
                      {despesa.data_vencimento && (
                        <div className="text-xs">
                          Vence: {formatDate(despesa.data_vencimento)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <AcoesDaLinha>
                        <BotaoVer onClick={() => navigate(`/despesas/${despesa.id}`)} title="Ver detalhes" />
                        {pode('despesas', 'editar') && <BotaoEditar onClick={() => navigate(`/despesas/${despesa.id}/editar`)} />}
                        {despesa.status !== 'paga' && pode('despesas', 'pagar') && (
                          <BotaoPagar
                            onClick={() => handleMarkAsPaid(despesa.id)}
                            disabled={markAsPaidMutation.isPending}
                            title="Marcar como paga"
                          />
                        )}
                        {pode('despesas', 'excluir') && <BotaoExcluir onClick={() => handleDelete(despesa.id)} disabled={deleteMutation.isPending} />}
                      </AcoesDaLinha>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {pagination && pagination.last_page > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Próximo
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando{' '}
                    <span className="font-medium">
                      {(pagination.current_page - 1) * pagination.per_page + 1}
                    </span>{' '}
                    até{' '}
                    <span className="font-medium">
                      {Math.min(pagination.current_page * pagination.per_page, pagination.total)}
                    </span>{' '}
                    de{' '}
                    <span className="font-medium">{pagination.total}</span>{' '}
                    resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      disabled={pagination.current_page === 1}
                      onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => handleFilterChange('page', page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === pagination.current_page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      disabled={pagination.current_page === pagination.last_page}
                      onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Próximo
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DespesasList;
